import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  author = 'Dr. Neha Dewan',
  publishedTime,
  section
}) => {
  const siteName = 'She Built This';
  const defaultTitle = 'She Built This — Documenting Women Who Built Empires';
  const defaultDescription = 'A research platform documenting 595+ verified women leaders across 7 continents. Founded by Dr. Neha Dewan. Every fact sourced. Every story told with precision.';
  const defaultImage = 'https://images.unsplash.com/photo-1758518727592-706e80ebc354?w=1200&q=80';
  const defaultKeywords = [
    'women in leadership',
    'female founders',
    'women entrepreneurs',
    'women CEOs',
    'gender equality',
    'women empowerment',
    'female executives',
    'women in business',
    'women leaders',
    'diversity in leadership',
    'women in tech',
    'female leadership',
    'women breaking barriers',
    'glass ceiling',
    'women role models',
    'successful women',
    'women in STEM',
    'female entrepreneurs UAE',
    'Middle East women leaders',
    'women board members',
    'corporate governance women',
    'gender gap',
    'women mentorship',
    'women sponsorship',
    'She Built This',
    'Dr. Neha Dewan'
  ];

  const seoTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoImage = image || defaultImage;
  const seoKeywords = keywords ? [...defaultKeywords, ...keywords] : defaultKeywords;
  const seoUrl = url || 'https://shebuiltthis.com';

  // Structured Data - Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "She Built This",
    "url": "https://shebuiltthis.com",
    "logo": "https://shebuiltthis.com/logo.png",
    "description": defaultDescription,
    "founder": {
      "@type": "Person",
      "name": "Dr. Neha Dewan",
      "jobTitle": "Founder & CEO",
      "description": "Researcher, governance specialist, and entrepreneur documenting women who built empires"
    },
    "sameAs": [
      "https://www.linkedin.com/company/shebuiltthis",
      "https://twitter.com/shebuiltthis",
      "https://www.instagram.com/shebuiltthis"
    ]
  };

  // Structured Data - WebSite with SearchAction
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "She Built This",
    "url": "https://shebuiltthis.com",
    "description": defaultDescription,
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://shebuiltthis.com/profiles?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // Article Schema (for blog posts)
  const articleSchema = type === 'article' ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": seoDescription,
    "image": seoImage,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "She Built This",
      "logo": {
        "@type": "ImageObject",
        "url": "https://shebuiltthis.com/logo.png"
      }
    },
    "datePublished": publishedTime || new Date().toISOString(),
    "articleSection": section || "Research"
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords.join(', ')} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:creator" content="@shebuiltthis" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
